import { shareFile, triggerDownload } from '@/src/core/utils/platformShare';
import { logger } from '@/src/core/utils';
import { Ionicons } from '@expo/vector-icons';
import { useRef, useState } from 'react';
import { ActivityIndicator, Modal, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import ViewShot from 'react-native-view-shot';

export interface ShareQRModalProps {
  visible: boolean;
  onClose: () => void;
  url: string;
  title: string;
  colors: {
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    primary: string;
    primaryText: string;
    overlay: string;
    border: string;
  };
}

export const ShareQRModal = ({ visible, onClose, url, title, colors }: ShareQRModalProps) => {
  const viewShotRef = useRef<ViewShot>(null);
  const [sharing, setSharing] = useState(false);

  const handleShareQR = async () => {
    try {
      setSharing(true);

      if (Platform.OS === 'web') {
        await handleWebShare();
      } else {
        // On mobile, use ViewShot
        if (!viewShotRef.current?.capture) return;
        const uri = await viewShotRef.current.capture();
        await shareFile({
          uri,
          filename: `${title.replace(/[^a-z0-9]/gi, '_')}_qr.png`,
          mimeType: 'image/png',
          dialogTitle: `Share ${title} QR Code`,
        });
      }
    } catch (error) {
      logger.error('ShareQR', 'Failed to share QR code', { error });
    } finally {
      setSharing(false);
    }
  };

  const handleWebShare = async () => {
    // Find the SVG element in the QR code
    const qrContainer = document.querySelector('[data-qr-container="true"]');
    if (!qrContainer) {
      logger.error('ShareQR', 'Failed to find QR container');
      return;
    }

    const svgElement = qrContainer.querySelector('svg');
    if (!svgElement) {
      logger.error('ShareQR', 'Failed to find SVG element');
      return;
    }

    try {
      // Create a canvas and draw the QR code with background and title
      const padding = 20;
      const qrSize = 200;
      const titleHeight = 40;
      const width = qrSize + padding * 2;
      const height = qrSize + padding * 2 + titleHeight;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Convert SVG to image and draw
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, padding, padding, qrSize, qrSize);
          URL.revokeObjectURL(svgUrl);

          // Add title text
          ctx.fillStyle = '#000000';
          ctx.font = '14px system-ui, -apple-system, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(title, width / 2, height - padding);

          resolve();
        };
        img.onerror = () => {
          URL.revokeObjectURL(svgUrl);
          reject(new Error('Failed to load QR SVG'));
        };
        img.src = svgUrl;
      });

      // Convert canvas to data URL
      const dataUrl = canvas.toDataURL('image/png');
      const filename = `${title.replace(/[^a-z0-9]/gi, '_')}_qr.png`;

      // Try to share, fallback to download
      const shared = await shareFile({
        uri: dataUrl,
        filename,
        mimeType: 'image/png',
        dialogTitle: `Share ${title} QR Code`,
      });

      if (!shared) {
        triggerDownload(dataUrl, filename);
      }
    } catch (error) {
      logger.error('ShareQR', 'Failed to share on web', { error });
      // Fallback: just copy URL to clipboard
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        alert('QR URL copied to clipboard!');
      }
    }
  };

  const qrContent = (
    <View style={[styles.qrContainer, { backgroundColor: '#fff' }]}>
      <QRCode value={url} size={200} backgroundColor="#fff" color="#000" />
      <Text style={styles.tournamentName}>{title}</Text>
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={[styles.overlay, { backgroundColor: colors.overlay }]}>
        <View style={[styles.modal, { backgroundColor: colors.surface }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={[styles.title, { color: colors.text }]}>Share Tournament</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="close" size={24} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* QR Code */}
          {Platform.OS === 'web' ? (
            <div data-qr-container="true">{qrContent}</div>
          ) : (
            <ViewShot ref={viewShotRef} options={{ format: 'png', quality: 1 }}>
              {qrContent}
            </ViewShot>
          )}

          {/* URL Preview */}
          <View style={[styles.urlContainer, { backgroundColor: colors.background, borderColor: colors.border }]}>
            <Ionicons name="link-outline" size={18} color={colors.textSecondary} />
            <Text style={[styles.urlText, { color: colors.textSecondary }]} numberOfLines={1}>
              {url}
            </Text>
          </View>

          {/* Share Button */}
          <TouchableOpacity
            style={[styles.shareButton, { backgroundColor: colors.primary }]}
            onPress={handleShareQR}
            disabled={sharing}
          >
            {sharing ? (
              <ActivityIndicator size="small" color={colors.primaryText} />
            ) : (
              <>
                <Ionicons name="share-outline" size={20} color={colors.primaryText} />
                <Text style={[styles.shareButtonText, { color: colors.primaryText }]}>
                  {Platform.OS === 'web' ? 'Download QR Code' : 'Share QR Code'}
                </Text>
              </>
            )}
          </TouchableOpacity>

          <Text style={[styles.hint, { color: colors.textSecondary }]}>
            Scan this QR code to view the tournament
          </Text>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 340,
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
  },
  header: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
  },
  qrContainer: {
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
  },
  tournamentName: {
    marginTop: 12,
    fontSize: 14,
    fontWeight: '600',
    color: '#000',
    textAlign: 'center',
    maxWidth: 180,
  },
  urlContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 16,
    gap: 8,
  },
  urlText: {
    flex: 1,
    fontSize: 12,
  },
  shareButton: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 10,
    marginTop: 16,
    gap: 8,
  },
  shareButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  hint: {
    marginTop: 12,
    fontSize: 12,
    textAlign: 'center',
  },
});
